import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { trackingService } from '../../../services/trackingService';
import StripeCardElement from './StripeCardElement';
import { bunny } from '../../../lib/bunnyNet';
import { authService } from '../../../services/supabaseService';
import { customerPortalService } from '../../../services/customerPortalService';
import EmailExistsModal from './EmailExistsModal';
import StepActions from './StepActions';
import { isQuoteService as checkQuoteService } from '../servicePricing';
import { sendCredentialsEmailSafe as sendCredentialsEmail } from '../../../utils/sendCredentialsEmail';
import { sendBookingRecapEmailSafe } from '../../../utils/sendBookingRecapEmail';

const BOOKING_DRAFT_KEY = 'plumberpro_booking_draft';

const PaymentStep = ({ user, data, currentStep, onUpdate, onComplete, onBack, onBookingComplete, isActive = true, showBack = true }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [customerInfo, setCustomerInfo] = useState({
    fullName: data?.fullName || `${data?.firstName || ''} ${data?.lastName || ''}`.trim(),
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    email: data?.email || '',
    phone: data?.phone || '',
    address: data?.address
      || [data?.streetLine, `${data?.postcode || ''} ${data?.city || ''}`.trim()].filter(Boolean).join(', ')
      || '',
    customerType: data?.customerType || 'individual',
    siret: data?.siret || '',
    companyName: data?.companyName || '',
    accessInfo: data?.accessInfo || '',
  });

  const [paymentMethod] = useState(data?.paymentMethod || 'card');
  const [createAccount] = useState(data?.createAccount ?? false);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [skipAccountCreation, setSkipAccountCreation] = useState(false);

  const isQuoteService = checkQuoteService(data?.service);

  const closeAcceptedQuoteIfNeeded = async () => {
    if (!data?.fromAcceptedQuote && !data?.service?.fromAcceptedQuote) return;

    const sourceAppointmentId = data?.sourceAppointmentId;
    if (!sourceAppointmentId) return;

    try {
      const { error } = await supabase
        .from('appointment_quote')
        .update({
          status: 'accepted',
          updated_at: new Date().toISOString(),
        })
        .eq('appointment_id', sourceAppointmentId)
        .in('status', ['accepted', 'sent', 'draft', 'rejected']);

      if (error) {
        console.error('Erreur clôture devis après réservation:', error);
      }
    } catch (err) {
      console.error('Exception clôture devis:', err);
    }
  };

  const finalizeBooking = async (updateData) => {
    await closeAcceptedQuoteIfNeeded();
    onUpdate(updateData);
    onBookingComplete?.();
    localStorage.removeItem(BOOKING_DRAFT_KEY);
    setShowSuccess(true);
  };

  const handleSuccessNavigation = () => {
    if (!user?.id) {
      navigate('/');
      return;
    }
    navigate(isQuoteService ? '/mes-devis' : '/public-service-tracking');
  };
  
  // ✅ Calculs de prix (recalculés automatiquement quand customerType change)
  const basePriceHT = data?.service?.basePrice || data?.service?.price || 0;
  const urgencyMultiplier = data?.urgency?.priceMultiplier || 1.0;
  const subtotalHT = basePriceHT * urgencyMultiplier;
  
  // TVA : devis accepté → taux du devis ; sinon selon type de client
  const quoteVat = Number(data?.service?.tva ?? data?.service?.TVA);
  const serviceTVA =
    data?.fromAcceptedQuote || data?.service?.fromAcceptedQuote
      ? (Number.isFinite(quoteVat) && quoteVat > 0
          ? quoteVat
          : customerInfo?.customerType === 'business'
            ? 20
            : 10)
      : customerInfo?.customerType === 'business'
        ? 20
        : 10;
  const vatRate = serviceTVA / 100;
  const vat = subtotalHT * vatRate;
  const total = subtotalHT + vat;

  // Garantie selon prestation
  const getWarrantyInfo = () => {
    const warrantyByService = {
      'Débouchage': '3 mois',
      'Fuite': '12 mois',
      'Installation': '24 mois',
      'Dépannage': '6 mois',
      'default': '6 mois'
    };
    const serviceName = data?.service?.name || 'default';
    return warrantyByService[serviceName] || warrantyByService.default;
  };

  // Fonction pour générer un mot de passe aléatoire
  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  // Fonction pour créer un compte utilisateur
  const createUserAccount = async () => {
    try {
      const fullName = customerInfo.fullName?.trim()
        || `${customerInfo.firstName} ${customerInfo.lastName}`.trim();
      const randomPassword = generateRandomPassword();
      
      console.log('Création du compte utilisateur pour:', customerInfo.email);
      
      const userDataToSend = {
        fullName: fullName,
        role: 'client',
        phone: customerInfo.phone,
      };

      const { data: userData, error } = await authService.CreateUser(
        customerInfo.email,
        randomPassword,
        userDataToSend
      );

      if (error) {
        throw new Error(error.message || 'Erreur lors de la création du compte');
      }

      console.log('✅ Compte client créé avec succès:', userData);
      
      // Envoyer l'email avec les identifiants
      const emailSent = await sendCredentialsEmail({
        email: customerInfo.email,
        password: randomPassword,
        fullName,
      });

      if (emailSent) {
        console.log('✅ Email d\'identification envoyé');
      } else {
        console.warn('⚠️ Échec de l\'envoi de l\'email, mais compte créé');
      }
      
      return { success: true, userId: userData?.user?.id, password: randomPassword };
      
    } catch (error) {
      console.error('❌ Erreur lors de la création du compte:', error);
      throw error;
    }
  };

  // Fonctions de formatage
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatSlotTime = () => {
    const raw = `${data?.timeSlot?.time || ''}`.trim();
    if (!raw) return '';
    const start = raw.split(' - ')[0]?.trim() || raw.split('-')[0]?.trim();
    return start || raw;
  };

  const formatAddress = () => {
    if (data?.address?.trim()) return data.address.trim();
    const parts = [];
    if (data?.streetLine) parts.push(data.streetLine);
    if (data?.addressComplement) parts.push(data.addressComplement);
    if (data?.postcode || data?.city) {
      const cityLine = [];
      if (data?.postcode) cityLine.push(data.postcode);
      if (data?.city) cityLine.push(data.city);
      parts.push(cityLine.join(' '));
    }
    return parts.join(', ');
  };

  const getCustomerDisplayName = () => {
    if (customerInfo?.customerType === 'business' && customerInfo?.companyName) {
      return customerInfo.companyName;
    }
    return customerInfo?.fullName?.trim()
      || `${customerInfo?.firstName || ''} ${customerInfo?.lastName || ''}`.trim()
      || 'Non renseigné';
  };

  const splitFullName = (fullName = '') => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '', lastName: '' };
    if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  };

  const getPaymentCustomerInfo = () => {
    const fullName = getCustomerDisplayName();
    const nameParts = splitFullName(customerInfo?.fullName?.trim() || fullName);
    const address = customerInfo?.address?.trim() || formatAddress();
    return {
      fullName,
      firstName: customerInfo?.firstName?.trim() || nameParts.firstName || data?.firstName || '',
      lastName: customerInfo?.lastName?.trim() || nameParts.lastName || data?.lastName || '',
      email: customerInfo?.email || data?.email,
      phone: customerInfo?.phone || data?.phone,
      address,
    };
  };

  // ✅ Log pour vérifier le recalcul automatique
  useEffect(() => {
    console.log('createAccount:', createAccount);
    console.log('photos: ', data?.photos);
    console.log('user: ', user);
    console.log('💰 Recalcul des prix:', {
      customerType: customerInfo?.customerType,
      TVA: `TVA ${serviceTVA}%`,
      subtotalHT: `${subtotalHT.toFixed(2)} €`,
      vat: `${vat.toFixed(2)} €`,
      total: `${total.toFixed(2)} €`
    });
  }, [customerInfo?.customerType, subtotalHT, serviceTVA, vat, total, createAccount, data?.photos, user]);

  const validateForm = () => {
    const newErrors = {};
    if (paymentMethod === 'card' && !isQuoteService && !cardReady) {
      newErrors.card = 'Veuillez renseigner une carte bancaire valide';
    }
    return newErrors;
  };

  // Fonction pour créer une intervention (utilisée pour les deux cas)
  const createAppointment = async (appointmentData) => {
    console.log('📋 Données à envoyer:', appointmentData);

    const { data: appointment, error: appointmentError } = await supabase
      .from('appointments')
      .insert(appointmentData)
      .select()
      .single();

    if (appointmentError) {
      console.error('Erreur Supabase:', appointmentError);
      throw new Error(appointmentError.message || 'Erreur lors de la création du rendez-vous');
    }

    console.log('Rendez-vous créé:', appointment);
    return appointment;
  };

  const updateExistingAppointment = async (appointmentId, appointmentData) => {
    console.log('📋 Mise à jour rendez-vous devis:', appointmentId, appointmentData);

    const { data: appointment, error: appointmentError } = await supabase
      .from('appointments')
      .update(appointmentData)
      .eq('id', appointmentId)
      .select()
      .single();

    if (appointmentError) {
      console.error('Erreur Supabase update:', appointmentError);
      throw new Error(appointmentError.message || 'Erreur lors de la mise à jour du rendez-vous');
    }

    console.log('Rendez-vous mis à jour:', appointment);
    return appointment;
  };

  const saveAppointmentForPayment = async (appointmentData) => {
    const fromAcceptedQuote = Boolean(data?.fromAcceptedQuote || data?.service?.fromAcceptedQuote);
    const sourceAppointmentId = data?.sourceAppointmentId || null;

    if (fromAcceptedQuote && sourceAppointmentId) {
      const { reference: _ignoredReference, ...updatePayload } = appointmentData;
      return updateExistingAppointment(sourceAppointmentId, {
        ...updatePayload,
        is_quote: false,
      });
    }

    return createAppointment(appointmentData);
  };

  /**
   * Upload une photo vers Bunny.net
   */
  const uploadPhoto = async (photo) => {
    try {
      if (photo?.file) {
        console.log('📤 Upload:', photo.file.name);
        const url = await bunny.uploadImage(photo.file);
        return url;
      }
      return null;
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      return null;
    }
  };

  /**
   * Traite toutes les photos
   */
  const processPhotos = async (photos) => {
    if (!Array.isArray(photos) || photos.length === 0) {
      return [];
    }
    
    console.log('🖼️ Traitement de', photos.length, 'photos');
    
    const urls = [];
    for (const photo of photos) {
      const url = await uploadPhoto(photo);
      if (url) urls.push(url);
    }
    
    console.log('✅ URLs obtenues:', urls);
    return urls;
  };

  const handleLoginRedirect = () => {
    onUpdate({ ...data, createAccount: false });
    const returnTo =
      data?.fromAcceptedQuote || data?.service?.fromAcceptedQuote
        ? '/quote-accept-booking'
        : '/service-booking-wizard';
    const params = new URLSearchParams({
      returnTo,
      step: data?.fromAcceptedQuote || data?.service?.fromAcceptedQuote
        ? 'payment'
        : String(currentStep ?? 8),
    });
    if (customerInfo.email?.trim()) {
      params.set('email', customerInfo.email.trim());
    }
    navigate(`/login-authentication?${params.toString()}`);
  };

  const handleContinueWithoutAccount = () => {
    setShowEmailExistsModal(false);
    setSkipAccountCreation(true);
    onUpdate({ ...data, createAccount: false });
    handleSubmit({ preventDefault: () => {} }, { skipAccountCreation: true });
  };

  const handleSubmit = async (e, options = {}) => {
    e?.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      setShowValidationSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setShowValidationSummary(false);
    setIsProcessing(true);

    try {
      let userId = user?.id || null;
      let newlyCreatedAccount = false;
      const shouldCreateAccount = createAccount && !user && !options.skipAccountCreation && !skipAccountCreation;

      if (shouldCreateAccount) {
        const emailExists = await customerPortalService.checkEmailExists(customerInfo.email);
        if (emailExists) {
          setShowEmailExistsModal(true);
          setIsProcessing(false);
          return;
        }
      }

      // Création du compte si demandé et si l'utilisateur n'est pas déjà connecté
      if (shouldCreateAccount) {
        console.log('📝 Création du compte client...');
        try {
          const result = await createUserAccount();
          userId = result.userId;
          newlyCreatedAccount = true;
          setAccountCreated(true);
          console.log('✅ Compte créé avec succès, userId:', userId);
        } catch (accountError) {
          console.error('❌ Erreur lors de la création du compte:', accountError);
          if (customerPortalService.isEmailAlreadyRegisteredError(accountError)) {
            setShowEmailExistsModal(true);
            setIsProcessing(false);
            return;
          }
          setErrors({
            submit: 'Erreur lors de la création du compte: ' + (accountError.message || 'Veuillez réessayer')
          });
          setShowValidationSummary(true);
          setIsProcessing(false);
          return;
        }
      }

      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '/');
      const { data: appointments, error: appointmentError } = await supabase
        .from('appointments')
        .select('*')
        .ilike('reference', `${today}-%`);

      let nombresappointements = appointments?.length || 0; 
      const reference = `${today}-${nombresappointements + 1}`;
      const slotDate = data?.timeSlot?.date ? new Date(data.timeSlot.date) : new Date();
      const [startTime, endTime] = data?.timeSlot?.time?.split(' - ') || ['09:00', '10:00'];
      
      // Recherche du technicien (sauf pour les devis)
      let assignedTechnicianId = null;
      if (!isQuoteService) {
        console.log('🔍 Recherche du meilleur technicien...');
        assignedTechnicianId = await trackingService.assignBestTechnician({
          scheduled_date: slotDate.toISOString().split('T')[0],
          scheduled_time_start: startTime,
          scheduled_time_end: endTime,
          service_type: data?.service?.name || 'Service',
          urgency: data?.urgency?.id || data?.urgency?.name || 'tomorrow',
          customer_latitude: parseFloat(data?.location?.lat ?? data?.lat ?? 48.8566),
          customer_longitude: parseFloat(data?.location?.lng ?? data?.lng ?? 2.3522),
        });

        if (!assignedTechnicianId) {
          console.warn('⚠️ Aucun technicien disponible');
          setErrors({ 
            submit: 'Aucun technicien disponible pour ce créneau. Veuillez choisir une autre date ou horaire.' 
          });
          setShowValidationSummary(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setIsProcessing(false);
          return;
        }

        console.log('✅ Technicien assigné:', assignedTechnicianId);
      }

      let appointmentResult = [];
      if (data?.service?.checklists && Array.isArray(data?.service?.checklists)) {
        appointmentResult.push({
          checklists: data?.service?.checklists,
          images: []
        });
      }

      // CAS 1: Service sur devis
      if (isQuoteService) {
        const appointmentData = {
          reference: reference,
          siret: customerInfo?.siret || null,
          companyName: customerInfo?.companyName || null,
          customer_name: getCustomerDisplayName(),
          customer_email: customerInfo?.email,
          customer_phone: customerInfo?.phone,
          customer_address: customerInfo?.address || formatAddress(),
          service_type: data?.service?.name || 'Service',
          urgency: null,
          status: 'planned',
          scheduled_date: null,
          scheduled_time_start: null,
          scheduled_time_end: null,
          price_subtotal: null,
          price_vat: null,
          tva: serviceTVA,
          price_total: null,
          notes: [
            data?.service?.otherMaterialRequested
              ? 'Matériel : le client souhaite un autre modèle que ceux proposés.'
              : null,
            data?.problemDescription || data?.description || '',
          ]
            .filter(Boolean)
            .join('\n'),
          customer_latitude: parseFloat(data?.location?.lat ?? data?.lat ?? 48.8566),
          customer_longitude: parseFloat(data?.location?.lng ?? data?.lng ?? 2.3522),
          Service: data?.service?.id,
          appointment_result: appointmentResult,
          technician: null,
          is_quote: true,
          client: userId || user?.id || null,
          accessInfo: customerInfo?.accessInfo || null,
        };
        
        if (data?.photos) {
          try {
            const processedPhotos = await processPhotos(data?.photos);
            appointmentData.photosclient = processedPhotos;
          } catch (error) {
            console.error('❌ Erreur lors du traitement des photos:', error);
            appointmentData.photosclient = data?.photos;
          }
        }
        
        const appointment = await createAppointment(appointmentData);

        // ✅ ENVOYER L'EMAIL RÉCAPITULATIF
        await sendBookingRecapEmailSafe({
          appointmentData: appointment,
          customerInfo,
          serviceData: data?.service,
          timeSlotData: data?.timeSlot,
          accountCreated: newlyCreatedAccount,
        });
        
        finalizeBooking({
          ...customerInfo,
          paymentMethod: 'sur_devis',
          createAccount,
          accountCreated: newlyCreatedAccount,
          userId: userId || null,
          total: 0,
          isQuote: true
        });
        
      } 
      // CAS 2: Paiement par carte
      else if (paymentMethod === 'card') {
        setStripeError(null);

        if (!stripe || !elements) {
          throw new Error('Stripe n\'est pas encore chargé. Veuillez réessayer dans quelques instants.');
        }

        const appointmentData = {
          reference: reference,
          siret: customerInfo?.siret || null,
          companyName: customerInfo?.companyName || null,
          customer_name: getCustomerDisplayName(),
          customer_email: customerInfo?.email,
          customer_phone: customerInfo?.phone,
          customer_address: customerInfo?.address || formatAddress(),
          service_type: data?.service?.name || 'Service',
          urgency: data?.urgency?.id || data?.urgency?.name || 'tomorrow',
          status: 'planned',
          scheduled_date: slotDate.toISOString().split('T')[0],
          scheduled_time_start: startTime,
          scheduled_time_end: endTime,
          price_subtotal: subtotalHT,
          price_vat: vat,
          tva: serviceTVA,
          price_total: total,
          notes: data?.problemDescription || data?.description || '',
          customer_latitude: parseFloat(data?.location?.lat ?? data?.lat ?? 48.8566),
          customer_longitude: parseFloat(data?.location?.lng ?? data?.lng ?? 2.3522),
          payment_status: 'pending',
          payment_method: 'card',
          Service: data?.service?.id,
          appointment_result: appointmentResult,
          technician: assignedTechnicianId,
          client: userId || user?.id || null,
          accessInfo: customerInfo?.accessInfo || null
        };
        
        if (data?.photos) {
          try {
            const processedPhotos = await processPhotos(data?.photos);
            appointmentData.photosclient = processedPhotos;
          } catch (error) {
            console.error('❌ Erreur lors du traitement des photos:', error);
            appointmentData.photosclient = data?.photos;
          }
        }
        
        const appointment = await saveAppointmentForPayment(appointmentData);
        const orderReference = appointment?.reference || reference;

        const amountInCents = Math.round(total * 100);
        
        const paymentRequestData = {
          orderData: {
            total: parseFloat(total.toFixed(2)),
            subtotal: parseFloat(subtotalHT.toFixed(2)),
            tax: parseFloat(vat.toFixed(2)),
            currency: 'EUR',
            reference: orderReference,
            items: [
              {
                name: data?.service?.name || 'Service de plomberie',
                price: parseFloat(total.toFixed(2)),
                quantity: 1
              }
            ]
          },
          customerInfo: {
            ...getPaymentCustomerInfo(),
            appointmentId: appointment.id
          }
        };

        console.log('Création du payment intent:', paymentRequestData);

        const response = await fetch(
          'https://nxvochdgdtnhbjbdckkw.supabase.co/functions/v1/create-payment-intent',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentRequestData),
          }
        );

        const responseText = await response.text();
        console.log('Réponse brute:', responseText);

        if (!response.ok) {
          let errorData;
          try {
            errorData = JSON.parse(responseText);
          } catch {
            errorData = { message: responseText };
          }
          console.error('Erreur API paiement:', errorData);
          throw new Error(errorData.error || errorData.message || 'Erreur lors de la création du paiement');
        }

        const paymentData = JSON.parse(responseText);
        console.log('Payment intent créé:', paymentData);

        const cardElement = elements.getElement(CardNumberElement);
        
        console.log('💳 Confirmation du paiement avec Stripe...');
        const { error: stripePaymentError, paymentIntent } = await stripe.confirmCardPayment(
          paymentData.clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: getCustomerDisplayName(),
                email: customerInfo?.email,
                phone: customerInfo?.phone,
                address: {
                  line1: customerInfo?.address,
                },
              },
            },
          }
        );

        if (stripePaymentError) {
          console.error('Stripe payment error:', stripePaymentError);
          throw new Error(stripePaymentError.message || 'Erreur lors du paiement');
        }

        console.log('Payment Intent Status:', paymentIntent.status);

        if (paymentIntent.status === 'succeeded') {
          console.log('✅ Paiement réussi!');
          
          const { error: updateError } = await supabase
            .from('appointments')
            .update({
              payment_intent_id: paymentData.paymentIntentId,
              payment_status: 'succeeded'
            })
            .eq('id', appointment.id);

          if (updateError) {
            console.error('Erreur mise à jour payment_intent_id:', updateError);
          } else {
            console.log('✅ Rendez-vous mis à jour avec le statut de paiement');
          }

          // ✅ ENVOYER L'EMAIL RÉCAPITULATIF
          await sendBookingRecapEmailSafe({
            appointmentData: appointment,
            customerInfo,
            serviceData: data?.service,
            timeSlotData: data?.timeSlot,
            accountCreated: newlyCreatedAccount,
          });

          finalizeBooking({
            ...customerInfo,
            paymentMethod,
            createAccount,
            accountCreated: newlyCreatedAccount,
            userId: userId || null,
            total,
          });
        } else {
          throw new Error('Le paiement n\'a pas été complété');
        }
      }

    } catch (error) {
      console.error('Erreur détaillée:', error);
      setStripeError(error.message);
      setErrors({ 
        submit: error.message || 'Erreur lors du traitement. Veuillez réessayer.' 
      });
      setShowValidationSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (showSuccess) {
    const slotDateLabel = formatDate(data?.timeSlot?.date);
    const slotTimeLabel = formatSlotTime();

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-fade-in">
          <div className="text-center">
            {isQuoteService && (
              <>
                <div className="w-20 h-20 rounded-full bg-warning/20 flex items-center justify-center mx-auto mb-6">
                  <Icon name="FileText" size={48} color="var(--color-warning)" />
                </div>
                
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  Demande de devis reçue !
                </h2>
                
                <p className="text-muted-foreground mb-6 text-justify">
                  Votre demande de devis a bien été reçue.
                  <br />
                  Vous recevrez votre proposition par e-mail dans les plus brefs délais.
                </p>
                
                {accountCreated && (
                  <div className="p-5 bg-gradient-to-r from-success/10 to-success/5 border-2 border-success/20 rounded-xl mb-6 text-left">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <Icon name="CheckCircle" size={20} className="text-success" />
                      <p className="font-semibold text-foreground">Compte créé !</p>
                    </div>
                    <p className="text-sm text-muted-foreground text-justify">
                      Votre compte a bien été créé.
                      <br />
                      Un mail contenant vos identifiants de connexion a été envoyé à{' '}
                      <strong className="font-bold text-foreground">{customerInfo?.email}</strong>.
                      <br />
                      Pensez à vérifier vos spams si vous ne trouvez pas le message dans votre boîte de réception.
                    </p>
                  </div>
                )}
                
                <button
                  onClick={handleSuccessNavigation}
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                >
                  {user?.id ? 'Suivre mon devis' : "Retour à l'accueil"}
                </button>
              </>
            )}    
            
            {!isQuoteService && (
              <>
                <div className="p-5 bg-gradient-to-r from-success/10 to-success/5 border-2 border-success/20 rounded-xl mb-6 text-left">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Icon name="CheckCircle" size={20} className="text-success" />
                    <p className="font-semibold text-foreground">Paiement confirmé !</p>
                  </div>
                  <p className="text-sm text-muted-foreground text-justify">
                    Votre paiement a bien été pris en compte.
                  </p>
                </div>

                {accountCreated && (
                  <div className="p-5 bg-gradient-to-r from-success/10 to-success/5 border-2 border-success/20 rounded-xl mb-6 text-left">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <Icon name="CheckCircle" size={20} className="text-success" />
                      <p className="font-semibold text-foreground">Compte créé !</p>
                    </div>
                    <p className="text-sm text-muted-foreground text-justify">
                      Votre compte a bien été créé.
                      <br />
                      Un mail contenant vos identifiants de connexion a été envoyé à{' '}
                      <strong className="font-bold text-foreground">{customerInfo?.email}</strong>.
                      <br />
                      Pensez à vérifier vos spams si vous ne trouvez pas le message dans votre boîte de réception.
                    </p>
                  </div>
                )}

                <div className="p-5 bg-gradient-to-r from-warning/10 to-warning/5 border-2 border-warning/20 rounded-xl text-left">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Icon name="Calendar" size={20} className="text-warning" />
                    <p className="font-semibold text-foreground">Rendez-vous confirmé !</p>
                  </div>
                  <p className="text-sm text-muted-foreground text-justify">
                    Un technicien se rendra chez vous le {slotDateLabel || '—'}
                    {slotTimeLabel ? ` à ${slotTimeLabel}` : ''}.
                  </p>
                  <p className="text-sm text-muted-foreground text-justify mt-2">
                    Un email récapitulatif vous sera envoyé à{' '}
                    <strong className="font-bold text-foreground">{customerInfo?.email}</strong>
                  </p>
                </div>
                
                <br />
                <button
                  onClick={handleSuccessNavigation}
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                >
                  {user?.id ? 'Suivre ma demande' : "Retour à l'accueil"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const errorCount = Object.keys(errors)?.length;

  return (
    <div className="max-w-5xl mx-auto">
      <EmailExistsModal
        isOpen={showEmailExistsModal}
        email={customerInfo.email}
        onLogin={handleLoginRedirect}
        onContinueWithoutAccount={handleContinueWithoutAccount}
        onChangeEmail={() => {
          setShowEmailExistsModal(false);
          onBack(true);
        }}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Récapitulatif */}
        <div className="lg:col-span-1 order-1">
          <div className="bg-card rounded-lg shadow-md p-4 lg:sticky lg:top-24">
            <h3 className="text-sm font-semibold text-foreground mb-3">Récapitulatif</h3>

            <div className="space-y-2 text-sm">
              <div className="pb-2 border-b border-border">
                <p className="text-xs text-muted-foreground">Prestation</p>
                <p className="font-medium text-foreground leading-snug">{data?.service?.name || 'Non renseigné'}</p>
                {!isQuoteService && data?.service?.selectedMaterial && (
                  <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    <div className="flex justify-between gap-2">
                      <span>Prestation (sans matériel)</span>
                      <span className="whitespace-nowrap">{Number(data.service.prixSansMateriel || 0).toFixed(2)} € HT</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="min-w-0 truncate">Matériel : {data.service.selectedMaterial.nom}</span>
                      <span className="whitespace-nowrap">+ {Number(data.service.selectedMaterial.prix || 0).toFixed(2)} € HT</span>
                    </div>
                  </div>
                )}
              </div>

              {!isQuoteService && (
              <div className="pb-2 border-b border-border">
                <p className="text-xs text-muted-foreground">Créneau</p>
                <p className="font-medium text-foreground leading-snug">
                  {data?.timeSlot?.date ? formatDate(data.timeSlot.date) : '—'}
                  {data?.timeSlot?.time ? ` · ${data.timeSlot.time}` : ''}
                </p>
              </div>
              )}

              <div className="pb-2 border-b border-border">
                <p className="text-xs text-muted-foreground">Adresse</p>
                <p className="font-medium text-foreground leading-snug line-clamp-2">
                  {formatAddress() || 'Non renseigné'}
                </p>
              </div>

              {!isQuoteService ? (
                <div className="pt-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm font-semibold text-foreground">Total</span>
                    <span className="text-xl font-bold text-primary">{total?.toFixed(2)} €</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    dont TVA {vat?.toFixed(2)} €
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Formulaire de paiement */}
        <div className="lg:col-span-2 order-2">
          <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 lg:p-8">
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-1">
                {isQuoteService ? 'Demande de devis' : 'Finalisez votre commande'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isQuoteService 
                  ? 'Un technicien vous contactera pour établir un devis personnalisé' 
                  : 'Réglez en toute sécurité pour confirmer votre intervention'}
              </p>
            </div>

            {!user && !createAccount && (
              <div className="mb-6 p-4 rounded-lg border border-border bg-muted/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Déjà un compte ?</p>
                  <p className="text-sm text-muted-foreground">
                    Connectez-vous pour lier cette réservation à votre compte client.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  iconName="LogIn"
                  iconPosition="left"
                  onClick={handleLoginRedirect}
                  className="flex-shrink-0"
                >
                  Se connecter
                </Button>
              </div>
            )}

            {showValidationSummary && errorCount > 0 && (
              <div className="mb-6 p-4 bg-error/10 border-2 border-error/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={24} color="var(--color-error)" className="flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-error mb-2">
                      {errorCount} {errorCount === 1 ? 'erreur détectée' : 'erreurs détectées'}
                    </h3>
                    <ul className="space-y-1">
                      {errors?.card && <li className="text-sm text-error/90">• {errors?.card}</li>}
                      {errors?.submit && <li className="text-sm text-error/90">• {errors?.submit}</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground mb-1">
                {isQuoteService ? 'Demande au nom de' : 'Commande au nom de'}
              </p>
              <p className="font-medium text-foreground">
                {getCustomerDisplayName()}
              </p>
              <p className="text-sm text-muted-foreground">{customerInfo?.email}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {!isQuoteService && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Informations de paiement
                  </label>
                  <div className="p-4 rounded-lg border-2 border-primary bg-primary/5">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon name="CreditCard" size={24} className="text-primary" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">Carte bancaire</p>
                        <p className="text-xs text-muted-foreground">Paiement sécurisé par Stripe</p>
                      </div>
                    </div>
                    
                    <StripeCardElement 
                      onPaymentMethodReady={setCardReady}
                      disabled={isProcessing}
                    />

                    {errors?.card && (
                      <div className="flex items-start gap-2 mt-3">
                        <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                        <p className="text-sm text-error">{errors?.card}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isQuoteService && (
                <div className="p-4 bg-muted/30 rounded-lg border border-border">
                  <p className="text-sm font-medium text-foreground mb-1">
                    Prestation sur devis
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Un devis gratuit vous sera envoyé après le diagnostic.
                  </p>
                </div>
              )}

              <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-6">
                <Button 
                  variant="outline" 
                  size="lg" 
                  iconName="ArrowLeft" 
                  onClick={onBack} 
                  type="button" 
                  disabled={isProcessing}
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  iconName={isProcessing ? 'Loader' : (isQuoteService ? 'Send' : 'CheckCircle')}
                  iconPosition="right"
                  loading={isProcessing}
                  disabled={isProcessing || (!isQuoteService && !stripe)}
                >
                  {isProcessing
                    ? 'Traitement...'
                    : isQuoteService
                    ? 'Demander un devis' 
                    : !stripe
                    ? 'Chargement du paiement...'
                    : 'Confirmer et payer'}
                </Button>
              </StepActions>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;